import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Body, Button, Tooltip, Bold } from '@panwds/react-ui'
import { Select } from '@panwds/react-form'
import { useFormContext } from 'react-hook-form'
import { InfoIcon } from '@panwds/icons'
import type { ReactNode } from 'react'
import type { SelectItem } from '@panwds/react-ui'

export interface IDropDownSize {
  height?: number;
  width?: number;
}
export interface IDropDownDisplayProperties {
  errorMessage?: string;
  toolTipMessage?: string;
  subTitle?: string;
}
export interface IDropDownProperties {
  required?: boolean;
  addNewValue?: boolean;
  addNewProps?: {
    conditionValue: string;
    updatedValue: string
  };
  triggerValidationOnChange?: boolean;
}
export interface IDropDownCallBacks {
  formatOptionsCallback?: (item: SelectItem) => ReactNode
  changeHandlerCallback?: object;
  customValidationCallback?: object;
}
export interface IDropDownData {
  purchased_size?: any ;
  tenant_id?: string;
  license_type?: string;
  key?: string;
  value: string;
  description?: string;
  iconLeft?: object;
  id?: string;
  additional_region_mapping?: any
  [key: string]: any
}
export interface IDropDownPassByProps {
  enableSearch?: boolean;
  label: string;
  placeholder?: string;
  extra?: object;
  dataTestId: string;
  disabled?: boolean;
}
export interface IDropDownProps {
  dropDownList: IDropDownData[];
  size: IDropDownSize;
  display: IDropDownDisplayProperties;
  dropDownProps: IDropDownProperties;
  callbacks: IDropDownCallBacks;
  passByProps: IDropDownPassByProps,
  fieldName: string;
  isLoading: boolean;
  disabled?: boolean;
}
export const ButtonWrapper = styled.div`
  & button {
    color: #1D2124;
    height: ${(props: IDropDownSize) => (props.hasOwnProperty('height') ? `${props.height}px` : '32px')};
    width: ${(props: IDropDownSize) => (props.hasOwnProperty('width') ? `${props.width}px` : '355px')};
    overflow:hidden;
  }
  display:flex;
  flex-direction:column
`

const DropDown = (props: IDropDownProps) => {
  const {
    dropDownList,
    size,
    display,
    dropDownProps,
    callbacks,
    fieldName,
    isLoading,
    disabled = false,
    passByProps } = props
  const {
    required = false,
    triggerValidationOnChange = false
  } = dropDownProps
  const {
    formatOptionsCallback,
    changeHandlerCallback,
    customValidationCallback,
  } = callbacks
  const {
    errorMessage,
    toolTipMessage = '',
    subTitle = ''
  } = display

  const { label, extra, dataTestId, ..._dropDownProps } = passByProps
  const { trigger, setError, formState, getValues, watch } = useFormContext()
  const { isDirty } = formState
  const { height = 400, width = 40 } = size
  const [selectedItem, setSelectedItem] = useState<IDropDownData>({ id: '', value: '' })
  // const watchField = watch(fieldName)
  const onChangeHandler = useCallback((value: IDropDownData) => {
    setSelectedItem(value)
    if (typeof changeHandlerCallback === 'function') {
      changeHandlerCallback(value)
    }
    triggerValidationOnChange && trigger(fieldName).then(isValid => !isValid && setError(fieldName, { type: 'focus', message: errorMessage }, { shouldFocus: true }))
  }, [changeHandlerCallback, errorMessage, fieldName, setError, trigger, triggerValidationOnChange])


  const validationHandler = useCallback((value: IDropDownData) => {
    //fieldName === 'cdl' && console.log('Inside the typeof customValidationCallback::', value, (!isLoading && isDirty && required && (!value || value?.id === ''), errorMessage))
    if (!isLoading && isDirty && required && (!value || value?.id === '')) {
      return errorMessage || `Please select the ${fieldName}`
    }
    if (!isLoading && isDirty && typeof customValidationCallback === 'function') {
      return customValidationCallback(value)
    }
    return true
  }, [customValidationCallback, errorMessage, fieldName, isDirty, isLoading, required])

  // useEffect(() => {
  //   if (watchField) {
  //     setSelectedItem(getValues(fieldName))
  //   }
  //   validationHandler(selectedItem)
  // }, [dropDownList, fieldName, getValues, required, selectedItem, validationHandler, watchField])

  return <>
    <div className='tw-flex tw-flex-row'>
      <Body size="sm" addClassName='tw-items-center tw-py-2 tw-pl-1'><Bold appearance="semibold">{label}</Bold></Body>
      {toolTipMessage && <Tooltip
        label={toolTipMessage}
      >
        <div className='tw-flex tw-items-center'>
          <Button
            size='sm'
            aria-label='contract-button'
            icon={<InfoIcon size="xs" className="tw-text-icon-gray" />}
            appearance='clear'
            style={{ background: 'transparent' }}
          />
        </div>
      </Tooltip>}
    </div>
    <ButtonWrapper width={width} height={height}>
      <Select
        addClassName='cmm-modal-select'
        items={dropDownList}
        label={label}
        showLabel={false}
        name={fieldName}
        dataTestId={dataTestId}
        defaultValue={selectedItem}
        isLoading={isLoading}
        disabled={disabled}
        fitToItemWidth
        register={{
          required: false,
          onChange: (event) => {
            onChangeHandler(event.target.value)
            trigger()
          },
          validate: value => validationHandler(value)
        }}
        {...{
          ..._dropDownProps,
          formatOption: {
            view: formatOptionsCallback
          },
          ...extra
        }}
      />
      {subTitle && <div className="tw-flex-1"><Body size="sm" addClassName="tw-my-1" appearance="secondary">{subTitle}</Body></div>}
    </ButtonWrapper>
  </>
}

export default DropDown
